import type { INotificationPreferences } from '@hit-game/shared/types';
import { Bell, Loader2, Settings as SettingsIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { PageHeader } from '../../components/account/PageHeader';
import { Toggle } from '../../components/common/Toggle';
import { TelegramLinkCard } from '../../components/settings/TelegramLinkCard';
import { userService } from '../../services/user.service';

/**
 * Danh sách toggle màn này biết render — đây là NGUỒN SỰ THẬT, không phải
 * response của server.
 *
 * Lặp theo `Object.keys(prefs)` (bản cũ) nghĩa là render theo bất cứ key nào
 * server trả về: user cũ còn sót preference đã gỡ thì tra nhãn ra `undefined`
 * và cả trang trắng màn. Lặp theo bảng này thì key lạ bị bỏ qua một cách vô hại.
 */
const NOTIFICATIONS: Record<keyof INotificationPreferences, { label: string; desc: string }> = {
  jackpotAlert: { label: 'Báo nổ hũ', desc: 'Thông báo khi tài khoản của bạn thực sự nổ hũ' },
  jackpotMonitor: {
    label: 'Theo dõi giá hũ',
    desc: 'Gửi thông báo giá trị hũ định kỳ 10 phút/lần',
  },
  lowBalance: { label: 'Số dư thấp', desc: 'Cảnh báo khi số dư tài khoản xuống thấp' },
  autoStop: { label: 'Dừng nhóm', desc: 'Thông báo khi nhóm bị dừng do hết tiền' },
  sessionReport: {
    label: 'Báo cáo cuối phiên',
    desc: 'Mỗi phiên 1 tin: kết quả, số dư trước/sau và lãi lỗ của mọi nhóm đã đánh',
  },
  proxyAlert: {
    label: 'Cảnh báo proxy chết',
    desc: 'Báo ngay khi proxy của tài khoản ngừng hoạt động — kèm acc game và nhóm bị ảnh hưởng',
  },
};

const KEYS = Object.keys(NOTIFICATIONS) as (keyof INotificationPreferences)[];

export function SettingsPage() {
  const [prefs, setPrefs] = useState<INotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    userService
      .getNotificationPreferences()
      .then((data) => {
        if (!cancelled) setPrefs(data);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Cập nhật lạc quan: lật công tắc ngay rồi mới gọi API, lỗi thì lật lại.
   * Chờ round-trip mới đổi giao diện làm công tắc có cảm giác "kẹt".
   */
  const toggle = (key: keyof INotificationPreferences) => {
    if (!prefs || saving) return;

    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSaving(true);

    void userService
      .updateNotificationPreferences({ [key]: next[key] })
      .then((saved) => {
        setPrefs(saved);
      })
      .catch(() => {
        setPrefs(prefs);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <div className="text-ink max-w-2xl space-y-6">
      <PageHeader
        icon={SettingsIcon}
        title="Cài đặt"
        subtitle="Cấu hình thông báo và tuỳ chọn cá nhân"
      />

      <div className="border-line bg-panel rounded-xl border p-6">
        <div className="mb-4 flex items-center gap-2">
          <Bell size={18} className="text-accent" />
          <h2 className="text-ink text-[15px] font-bold">Thông báo</h2>
          {saving && <Loader2 size={14} className="text-accent animate-spin" />}
        </div>

        {loading || !prefs ? (
          <div className="text-ink-dim flex items-center gap-2 py-4 text-[12px]">
            <Loader2 size={14} className="animate-spin" />
            Đang tải cấu hình...
          </div>
        ) : (
          <div className="space-y-3">
            {KEYS.map((key) => (
              <Toggle
                key={key}
                checked={prefs[key]}
                onChange={() => {
                  toggle(key);
                }}
                label={NOTIFICATIONS[key].label}
                description={NOTIFICATIONS[key].desc}
                disabled={saving}
              />
            ))}
          </div>
        )}
      </div>

      <TelegramLinkCard />
    </div>
  );
}
